import { useEffect, useRef } from 'react';
import type maplibregl from 'maplibre-gl';

import { repositionSimulator } from '@/api/sim';
import type { MapController } from '@/map/MapCanvas';
import { beginReposition, endReposition } from '@/map/repositioning';
import { useDroneStore } from '@/stores/droneStore';
import type { DroneId } from '@/types/domain';

type Member = { id: DroneId; lat: number; lon: number };

// Commit to the simulator backend at most this often while dragging. The sim is
// stationary between commits, so its returning frames only ever pull the icon
// back to the last committed point — a few metres at drag speed — and the next
// local move corrects it. Fast enough to feel live, sparse enough not to flood
// the box with a POST per mousemove.
const COMMIT_MS = 90;

// How long after the drop a drone is still treated as being placed. Covers a
// telemetry frame already in flight from before the final commit; shorter than
// this and a late frame draws a trail back to where the aircraft used to be.
const REPOSITION_SETTLE_MS = 1500;

/**
 * Drag a simulator across the map, and ctrl/⌘-drag to move the whole selection.
 * Shared by Command and Planning so the gesture is identical on both screens.
 *
 * Simulators only. Dragging a real aircraft's icon would be a goto — flying it
 * somewhere — and that belongs behind the explicit GOTO affordance with its
 * confirmation, not a stray mouse gesture. Real aircraft are left alone here.
 *
 * The whole fleet moves by the same delta rather than stacking on the cursor: a
 * formation dragged across the map should arrive still in formation. Icons move
 * in the local store every frame so they track the cursor with no round-trip;
 * the backend is committed on a throttle plus once on release.
 */
export function useSimDrag(controller: MapController, onMessage: (text: string) => void): void {
  // Held in a ref so a new onMessage identity each render does not tear down and
  // re-attach the map listeners — that would abort a drag in progress.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  // Attach through controller.ready, NOT controller.raw(): raw() is null until
  // the map container mounts, and this effect runs before that — so gating on
  // raw() meant the listeners were never added and every drag just panned the
  // map. ready() runs the setup once the map is usable and again if it rebuilds.
  useEffect(() => {
    return controller.ready(() => {
      const map = controller.raw();
      if (!map) return undefined;

      let drag: { startLat: number; startLon: number; members: Member[] } | null = null;

      const simsIn = (ids: DroneId[]): Member[] => {
        const out: Member[] = [];
        for (const id of ids) {
          const drone = useDroneStore.getState().drones.get(id);
          const { lat, lon } = drone?.scalars ?? {};
          if (!drone?.simulated || typeof lat !== 'number' || typeof lon !== 'number') continue;
          out.push({ id, lat, lon });
        }
        return out;
      };

      const placeLocally = (members: Member[], dLat: number, dLon: number) => {
        const observedAt = Date.now();
        useDroneStore.getState().applyDeltas(
          members.map((member) => ({
            droneId: member.id,
            scalars: { lat: member.lat + dLat, lon: member.lon + dLon },
            telemetry: {},
            observedAt,
          })),
        );
      };

      const commit = (members: Member[], dLat: number, dLon: number) => {
        for (const member of members) {
          void repositionSimulator(member.id, member.lat + dLat, member.lon + dLon);
        }
      };

      let lastCommit = 0;

      const onDown = (event: MouseEvent) => {
        const root = (event.target as HTMLElement | null)?.closest?.('.dm-marker');
        const id = root?.getAttribute('data-drone-id') as DroneId | null;
        if (!id) return;
        const drone = useDroneStore.getState().drones.get(id);
        if (!drone?.simulated) return;

        // Ctrl/⌘ takes the whole selection with it; a plain drag moves just this
        // one, whatever else happens to be selected.
        const selected = useDroneStore.getState().selectedIds;
        const wanted = (event.ctrlKey || event.metaKey) && selected.includes(id) ? selected : [id];
        const members = simsIn(wanted);
        if (members.length === 0) return;

        const anchorAt = members.find((m) => m.id === id) ?? members[0]!;
        drag = { startLat: anchorAt.lat, startLon: anchorAt.lon, members };
        // Tell the marker layer these are being placed, not flown, so the drag
        // does not leave a track trail claiming the aircraft flew the path the
        // cursor took.
        beginReposition(members.map((member) => member.id));
        event.preventDefault();
        event.stopPropagation();
        map.dragPan.disable();
      };

      const onMove = (event: maplibregl.MapMouseEvent) => {
        if (!drag) return;
        const dLat = event.lngLat.lat - drag.startLat;
        const dLon = event.lngLat.lng - drag.startLon;
        placeLocally(drag.members, dLat, dLon);
        const now = Date.now();
        if (now - lastCommit >= COMMIT_MS) {
          lastCommit = now;
          commit(drag.members, dLat, dLon);
        }
      };

      const onUp = (event: maplibregl.MapMouseEvent) => {
        if (!drag) return;
        const dLat = event.lngLat.lat - drag.startLat;
        const dLon = event.lngLat.lng - drag.startLon;
        // Land the icons and the backend on the exact drop point, whatever the
        // throttle last sent.
        placeLocally(drag.members, dLat, dLon);
        commit(drag.members, dLat, dLon);
        const moved = drag.members.length;
        const placed = drag.members.map((member) => member.id);
        drag = null;
        map.dragPan.enable();
        // Held open past the drop: a telemetry frame still in flight carries
        // the position from before the last commit, and releasing immediately
        // would let that late frame draw a line from there back to the drop
        // point — a trail for a journey that never happened.
        window.setTimeout(() => endReposition(placed), REPOSITION_SETTLE_MS);
        onMessageRef.current(
          moved === 1 ? 'Simulator repositioned' : `${moved} simulators repositioned`,
        );
      };

      const container = map.getContainer();
      container.addEventListener('mousedown', onDown, true);
      map.on('mousemove', onMove);
      map.on('mouseup', onUp);
      return () => {
        container.removeEventListener('mousedown', onDown, true);
        map.off('mousemove', onMove);
        map.off('mouseup', onUp);
      };
    });
  }, [controller]);
}
